import type { LegacyList } from '@mypoz/shared';

import { legacyApi } from '@/lib/legacy-api.js';

/**
 * Raised when a packages endpoint answers with anything but the payload it promised.
 *
 * Carries the vendor's own code so the console names the failure — `HTTP_502`, `TIMEOUT`,
 * `MALFORMED`, or one of the endpoint's documented business codes — while the screen shows a
 * translated line and a retry. Same split `WarehousesRequestError` draws, and for the same reason:
 * the vendor's `message` is Chinese prose from its own code table, useful in a log and wrong in
 * front of a customer.
 */
export class PackagesRequestError extends Error {
  constructor(
    readonly endpoint: string,
    readonly code: string,
    /**
     * The failure as the client resolved it: the endpoint's code table, else the vendor's own
     * `msg_en`/`msg_cn`, else a generic line (see `normaliseEnvelope`). Kept on its own so a screen
     * can show the vendor's wording for a refusal this app has no translation for (MYP-98).
     */
    readonly detail: string,
  ) {
    super(`${endpoint} failed (${code}): ${detail}`);
    this.name = 'PackagesRequestError';
  }
}

/**
 * Every package on the account, newest first.
 *
 * `limit: -1` because the vendor pages at ten by default and this list filters, counts and sorts
 * across the whole set client-side — a tab count computed over page one would be wrong. The
 * endpoint's own filter parameters (`status`, `goodstype`, `validStatus`) do work, but are
 * deliberately not used: `packages/shared` already owns that logic, it is unit-tested, and moving
 * it to the server would mean a round trip per filter change plus one query per tab count.
 */
export async function fetchPackageRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('package_list', { limit: -1 });

  if (!result.ok) throw new PackagesRequestError('package_list', result.code, result.message);

  return result.data.list;
}

/** One page of packages and the vendor's count for the whole filtered list (MYP-126). */
export interface PackagePage {
  readonly rows: readonly unknown[];
  readonly count: number;
}

/**
 * One page of `package_list`, filtered by the vendor (MYP-126) — see `packageListParams`.
 *
 * `count` is the vendor's total for the filter, not the rows returned. It has been seen one
 * higher than the rows that exist (310 for 309); the pager shows it as the vendor says it.
 */
export async function fetchPackagePage(params: Record<string, string>): Promise<PackagePage> {
  const result = await legacyApi.call<LegacyList<unknown>>('package_list', params);
  if (!result.ok) throw new PackagesRequestError('package_list', result.code, result.message);
  return { rows: result.data.list, count: result.data.count };
}

/**
 * The vendor's status dictionary — twenty-two codes with a Chinese and an English label each.
 *
 * Fetched rather than hardcoded because the document names only two of them and mislabels one of
 * those, and because the list is the vendor's to extend. `PACKAGE_STATUS` in this feature's
 * `dictionary.ts` used to stand in for it; this replaces it.
 */
export async function fetchPackageStatusRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('package_status_list');

  if (!result.ok) {
    throw new PackagesRequestError('package_status_list', result.code, result.message);
  }

  return result.data.list;
}

/**
 * The vendor's goods-type dictionary (MYP-102), as the keyed map it arrives in.
 *
 * Returns `keyed`, not `list`. The live endpoint keys every entry by its code —
 * `{"GENERAL": {"en": …, "zh": …, "cn": …}, …}` — and carries no code inside the value, so the
 * flattened `list` the envelope also offers would keep the labels and lose every code. The
 * document's `{id, name}` rows do not exist on the wire.
 *
 * A plain array where the map should be is treated as a failure, not an empty dictionary: it would
 * mean the vendor changed the shape, and the loud error screen is better than a filter that
 * quietly offers nothing.
 */
export async function fetchGoodsTypes(): Promise<Readonly<Record<string, unknown>>> {
  const result = await legacyApi.call<LegacyList<unknown>>('goods_type');

  if (!result.ok) throw new PackagesRequestError('goods_type', result.code, result.message);

  const { keyed, list } = result.data;
  if (keyed) return keyed;
  // `{count: 0}` with no list at all is a genuinely empty dictionary, not a changed shape.
  if (list.length === 0) return {};

  throw new PackagesRequestError('goods_type', 'MALFORMED', 'Expected goods types keyed by code');
}

/**
 * Every warehouse's package services (MYP-100), as the keyed map it arrives in — one array of
 * service rows per warehouse id. `keyed`, not `list`, for the same reason as `fetchGoodsTypes`:
 * the flattened list would drop which warehouse offers which service.
 *
 * An empty `[]` (how PHP encodes an empty map) means no services anywhere, not a failure. A
 * non-empty plain array would mean the vendor changed the shape, and is reported as one.
 */
export async function fetchPackageServices(): Promise<Readonly<Record<string, unknown>>> {
  const result = await legacyApi.call<LegacyList<unknown>>('packages_services');

  if (!result.ok) throw new PackagesRequestError('packages_services', result.code, result.message);

  const { keyed, list } = result.data;
  if (keyed) return keyed;
  if (list.length === 0) return {};

  throw new PackagesRequestError(
    'packages_services',
    'MALFORMED',
    'Expected services keyed by warehouse id',
  );
}

/**
 * Registers a parcel on the account (MYP-98) — `add_packages`, which the vendor also uses to
 * *claim* (认领) an inbound parcel by its tracking number.
 *
 * Returns nothing: the success body is `{code, transtype, msg_cn, msg_en}` with no package in it —
 * no id, no status, nothing to insert — so the caller refetches the list rather than inventing a
 * row. Build `params` with `toAddPackageParams`; this function only sends them.
 */
export async function addPackage(params: Readonly<Record<string, string>>): Promise<void> {
  const result = await legacyApi.call<{ message: string }>('add_packages', params);

  if (!result.ok) throw new PackagesRequestError('add_packages', result.code, result.message);
}

/**
 * Deletes one package from the account (MYP-99) — `delete_package`, by the vendor's own id.
 *
 * `pkgid` only, never `inorderno` as well: the document says "at least one", not what the vendor
 * does when given both — match either, or require both — and the id is the vendor's primary key,
 * unambiguous where a tracking number may repeat. Returns nothing; the body is
 * `{code, msg_cn, msg_en}`, so the caller refetches.
 */
export async function deletePackage(packageId: string): Promise<void> {
  const result = await legacyApi.call<{ message: string }>('delete_package', { pkgid: packageId });

  if (!result.ok) throw new PackagesRequestError('delete_package', result.code, result.message);
}

/**
 * One package's message thread with its warehouse.
 *
 * Fetched per package when the thread is opened rather than alongside the list: a customer reads
 * one thread at a time, and the endpoint takes a single package id.
 */
export async function fetchPackageMessageRows(packageId: string): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('get_packages_msg', { id: packageId });

  if (!result.ok) throw new PackagesRequestError('get_packages_msg', result.code, result.message);

  return result.data.list;
}

/**
 * Posts a customer reply on one package's message thread (MYP-101).
 *
 * `type`/`direct` are fixed rather than parameters: this endpoint only ever sends the customer's
 * own words to the warehouse, never the reverse, so there is nothing for a caller to get wrong by
 * passing them in. `recvuserid` has no recipient to name here either — `0` is the documented value
 * for "empty".
 *
 * The success response carries no payload worth reading (`flatMessage` — see `envelope.ts`), which
 * is exactly why this returns `void` rather than a posted `PackageMessage`: there is no real id or
 * server timestamp to hand back. The caller re-fetches the thread instead of trusting a local echo.
 */
export async function postPackageMessage(packageId: string, content: string): Promise<void> {
  const result = await legacyApi.call<{ message: string }>('post_packages_msg', {
    tid: packageId,
    type: 'PACKAGE',
    direct: 'U2W',
    content,
    recvuserid: 0,
  });

  if (!result.ok) throw new PackagesRequestError('post_packages_msg', result.code, result.message);
}
